import { useCallback, useEffect, useRef } from 'react'
import { appendFilterParams } from '@/hooks/inbox/useConversationList'
import type { ConversationListItem, Team } from '@/components/inbox/types'

// Debounce window for poke-pull syncs: WebSocket events only signal that
// something changed; the authoritative state (unread counts, stats, list)
// is fetched from /inbox/sync. Bursts of events collapse into one fetch.
const SYNC_DEBOUNCE_MS = 800

interface InboxSyncResponse {
  conversations: ConversationListItem[]
  stats: { all: number; assigned_to_me: number; unassigned: number; open: number; closed: number; snoozed: number }
  teams: Team[]
}

interface UseInboxSyncParams {
  filter: string
  statusFilter: string
  teamFilter: string | null
  channelFilter: string | null
  matchesCurrentFilter: (conv: ConversationListItem) => boolean
  setConversations: React.Dispatch<React.SetStateAction<ConversationListItem[]>>
  setStats: React.Dispatch<React.SetStateAction<{ all: number; assigned_to_me: number; unassigned: number; open: number; closed: number; snoozed: number }>>
  setTeams: React.Dispatch<React.SetStateAction<Team[]>>
}

// Merge the authoritative first page from the server with conversations the
// client accumulated via infinite scroll. Server items win (fresh unread
// counts and previews); older items beyond page one are kept as long as they
// still match the current filter.
export function mergeConversations(
  serverList: ConversationListItem[],
  prev: ConversationListItem[],
  matchesCurrentFilter: (conv: ConversationListItem) => boolean,
): ConversationListItem[] {
  const serverIds = new Set(serverList.map((c) => c.id))
  const tail = prev.filter((c) => !serverIds.has(c.id) && matchesCurrentFilter(c))
  return [...serverList, ...tail]
}

export function useInboxSync({
  filter,
  statusFilter,
  teamFilter,
  channelFilter,
  matchesCurrentFilter,
  setConversations,
  setStats,
  setTeams,
}: UseInboxSyncParams) {
  const timerRef = useRef<number | null>(null)

  const performSync = useCallback(async () => {
    try {
      const params = new URLSearchParams()
      appendFilterParams(params, filter, statusFilter, teamFilter, channelFilter)
      const queryString = params.toString()
      const response = await fetch(`/inbox/sync${queryString ? `?${queryString}` : ''}`)
      if (!response.ok) return
      const data: InboxSyncResponse = await response.json()
      setConversations((prev) => mergeConversations(data.conversations, prev, matchesCurrentFilter))
      setStats(data.stats)
      setTeams(data.teams)
    } catch {
      // Network error: the next poke (or reconnect) retries the sync
    }
  }, [filter, statusFilter, teamFilter, channelFilter, matchesCurrentFilter])

  // Schedule a debounced sync; pass immediate=true to skip the debounce
  // (used on WebSocket reconnect, where we want fresh state right away).
  const requestSync = useCallback(
    (immediate = false) => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
      if (immediate) {
        void performSync()
        return
      }
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null
        void performSync()
      }, SYNC_DEBOUNCE_MS)
    },
    [performSync]
  )

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  return { requestSync }
}
